import { WS_BASE_URL } from '../config.js';
import { getSafeErrorDetails } from '../utils/safeConsole.js';

const HUB_GLOBAL_KEY = '__bluezapConversationsHubState__';
const HUB_DEBUG_PREFIX = '[conversationsHub]';
const hubState = (() => {
  if (typeof globalThis === 'undefined') {
    return { channels: new Map(), subscriberSequence: 0 };
  }
  if (!globalThis[HUB_GLOBAL_KEY]) {
    globalThis[HUB_GLOBAL_KEY] = { channels: new Map(), subscriberSequence: 0 };
  }
  return globalThis[HUB_GLOBAL_KEY];
})();
const channels = hubState.channels;
const HEARTBEAT_INTERVAL_MS = 25000;
const MAX_RECONNECT_DELAY_MS = 30000;
const BASE_RECONNECT_DELAY_MS = 1000;
const MIN_CONNECT_ATTEMPT_INTERVAL_MS = 1800;
const RELEASE_GRACE_PERIOD_MS = 4000;
const RECONNECT_JITTER_RATIO = 0.2;
const DASHBOARD_CLIENT_ID_STORAGE_KEY = 'bluezap:dashboard-ws-client-id';
const NON_RECONNECT_CLOSE_CODES = new Set([1008, 4003, 4401, 4403]);
const STABLE_CONNECTION_MS = 10000;
const NON_RECONNECT_REASON_PATTERNS = [
  'cleanup',
  'auth',
  'logout',
  'unauthorized',
  'forbidden',
  'company mismatch',
  'invalid session',
  'stale_socket_replaced',
  'orphaned_after_open',
  'replaced_by_new_connection',
  'no_listeners_grace_elapsed',
];

const STATUS = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
};

const READY_STATE_LABELS = {
  0: 'CONNECTING',
  1: 'OPEN',
  2: 'CLOSING',
  3: 'CLOSED',
};

const isConversationsHubDebugEnabled = () =>
  import.meta.env.DEV === true && import.meta.env.VITE_DEBUG_CONVERSATIONS_HUB === 'true';

const HUB_DEBUG_ALLOWED_DETAIL_KEYS = new Set([
  'code',
  'closeCode',
  'delayMs',
  'hadPendingConnect',
  'incrementRetryCount',
  'internalReason',
  'lifecycle',
  'listenersAfter',
  'listenersBefore',
  'listenersSize',
  'origin',
  'pendingConnect',
  'reason',
  'readyState',
  'reconnectDelayMs',
  'reconnectTrigger',
  'retryCount',
  'reuseReason',
  'shouldReconnect',
  'status',
  'wasClean',
  'wasCurrentSocket',
  'willReconnect',
  'willScheduleGraceClose',
]);

const sanitizeHubDebugValue = (value) => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null
  ) {
    return value;
  }
  return undefined;
};

const sanitizeHubDebugDetails = (details = {}) => {
  if (!details || typeof details !== 'object') return {};
  return Object.entries(details).reduce((safeDetails, [key, value]) => {
    if (!HUB_DEBUG_ALLOWED_DETAIL_KEYS.has(key)) {
      return safeDetails;
    }
    const safeValue = sanitizeHubDebugValue(value);
    if (safeValue !== undefined) {
      safeDetails[key] = safeValue;
    }
    return safeDetails;
  }, {});
};

const normalizeCompanyId = (companyId) => {
  const value = Number(companyId);
  if (!Number.isFinite(value)) return null;
  return Math.trunc(value);
};

const generateClientId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const generateSubscriberId = () => {
  hubState.subscriberSequence = Number(hubState.subscriberSequence || 0) + 1;
  return `sub-${hubState.subscriberSequence}`;
};

const resolveReadyState = (ws) => {
  if (!ws) return 'NO_SOCKET';
  const readyState = ws.readyState;
  return READY_STATE_LABELS[readyState] || String(readyState);
};

const debugHubEvent = (event, details = {}) => {
  if (!isConversationsHubDebugEnabled()) return;
  try {
    console.debug(`${HUB_DEBUG_PREFIX} ${event}`, sanitizeHubDebugDetails(details));
  } catch {
    // Ignore logging errors.
  }
};

const debugChannelEvent = (event, channel, details = {}) => {
  debugHubEvent(event, {
    listenersSize: channel?.listeners?.size ?? 0,
    readyState: resolveReadyState(channel?.ws),
    status: channel?.status ?? null,
    pendingConnect: channel?.pendingConnect ?? false,
    pendingRelease: channel?.pendingRelease ?? false,
    ...details,
  });
};

const resolveDashboardClientId = () => {
  if (typeof window === 'undefined') {
    const generated = generateClientId();
    debugHubEvent('clientId:create:server');
    return generated;
  }

  try {
    const existing = window.sessionStorage.getItem(DASHBOARD_CLIENT_ID_STORAGE_KEY);
    if (existing) {
      debugHubEvent('clientId:reuse');
      return existing;
    }
    const created = generateClientId();
    window.sessionStorage.setItem(DASHBOARD_CLIENT_ID_STORAGE_KEY, created);
    debugHubEvent('clientId:create');
    return created;
  } catch {
    const fallback = generateClientId();
    debugHubEvent('clientId:create:fallback');
    return fallback;
  }
};

const buildWsUrl = (companyId, clientId) => {
  const params = new URLSearchParams({
    company_id: String(companyId),
    client_id: clientId,
  });
  return `${WS_BASE_URL}/ws/conversations?${params.toString()}`;
};

const getChannelClientId = (channel) => {
  if (channel?.clientId) return channel.clientId;
  const clientId = resolveDashboardClientId();
  if (channel) {
    channel.clientId = clientId;
  }
  return clientId;
};

const toPayload = (rawData) => {
  if (typeof rawData !== 'string') return rawData;
  const trimmed = rawData.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return { type: 'raw_text_message', raw: rawData };
  }
};

const safeCall = (fn, arg) => {
  if (typeof fn !== 'function') return;
  try {
    fn(arg);
  } catch (error) {
    console.error('ConversationsHub subscriber callback failed.', getSafeErrorDetails(error));
  }
};

const notifyStatus = (channel) => {
  for (const listener of channel.listeners) {
    safeCall(listener.onStatus, channel.status);
  }
};

const notifyMessage = (channel, payload) => {
  if (!payload) return;
  for (const listener of channel.listeners) {
    safeCall(listener.onMessage, payload);
  }
};

const clearReconnectTimer = (channel, reason = 'unspecified') => {
  if (!channel.reconnectTimer) return;
  clearTimeout(channel.reconnectTimer);
  debugChannelEvent('cancelReconnect', channel, {
    reason,
    reconnectTimerId: channel.reconnectTimerId,
  });
  channel.reconnectTimer = null;
  channel.reconnectTimerId = null;
};

const clearHeartbeatTimer = (channel) => {
  if (!channel.heartbeatTimer) return;
  clearInterval(channel.heartbeatTimer);
  channel.heartbeatTimer = null;
};

const startHeartbeatTimer = (channel, ws) => {
  clearHeartbeatTimer(channel);
  channel.heartbeatTimer = setInterval(() => {
    try {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    } catch {
      // Ignore heartbeat errors; reconnect is handled on close.
    }
  }, HEARTBEAT_INTERVAL_MS);
};

const clearReleaseTimer = (channel, reason = 'unspecified') => {
  if (!channel?.releaseTimer) return;
  clearTimeout(channel.releaseTimer);
  debugChannelEvent('releaseChannel:cancel-close', channel, {
    reason,
    releaseTimerId: channel.releaseTimerId,
  });
  channel.releaseTimer = null;
  channel.releaseTimerId = null;
  channel.pendingRelease = false;
};

const setCloseIntent = (
  channel,
  { internalReason, closeCode = null, origin = 'unknown', source = null, subscriberId = null } = {}
) => {
  const closeIntent = {
    internalReason: internalReason || 'cleanup',
    closeCode,
    origin,
    source,
    subscriberId,
    createdAt: Date.now(),
  };
  if (channel) {
    channel.closeIntent = closeIntent;
    channel.manualCloseReason = closeIntent.internalReason;
  }
  return closeIntent;
};

const clearCloseIntent = (channel) => {
  if (!channel) return;
  channel.closeIntent = null;
};

const closeTrackedSocket = (
  channel,
  ws,
  { internalReason = 'cleanup', closeCode = 1000, origin = 'unknown', source = null, subscriberId = null } = {}
) => {
  const closeIntent = setCloseIntent(channel, {
    internalReason,
    closeCode,
    origin,
    source,
    subscriberId,
  });
  debugChannelEvent('socket:close-called', channel, {
    internalReason: closeIntent.internalReason,
    closeCode,
    origin,
    source,
    subscriberId,
    listenersSize: channel?.listeners?.size ?? 0,
    readyState: resolveReadyState(ws),
  });
  if (closeCode === null || closeCode === undefined) {
    ws.close();
    return;
  }
  ws.close(closeCode, closeIntent.internalReason);
};

const isSocketOpenOrConnecting = (ws) =>
  Boolean(ws) && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING);

const applyReconnectJitter = (delay) => {
  const safeDelay = Number.isFinite(delay) && delay > 0 ? delay : BASE_RECONNECT_DELAY_MS;
  const jitterWindow = Math.max(250, Math.floor(safeDelay * RECONNECT_JITTER_RATIO));
  return safeDelay + Math.floor(Math.random() * jitterWindow);
};

const beginConnectAttempt = (channel, socketId, reason) => {
  const startedAt = Date.now();
  channel.connectInFlight = {
    socketId,
    reason,
    startedAt,
  };
  channel.lastConnectAttemptAt = startedAt;
};

const clearConnectInFlight = (channel, socketId, lifecycle, details = {}) => {
  if (!channel?.connectInFlight) return false;
  if (channel.connectInFlight.socketId !== socketId) {
    return false;
  }
  channel.connectInFlight = null;
  debugChannelEvent('connectInFlight:clear', channel, {
    socketId,
    lifecycle,
    ...details,
  });
  return true;
};

const shouldReconnectForCloseEvent = (event) => {
  const code = Number(event?.code);
  if (NON_RECONNECT_CLOSE_CODES.has(code)) {
    return false;
  }

  const reason = String(event?.reason || '').trim().toLowerCase();
  if (!reason) {
    return true;
  }

  return !NON_RECONNECT_REASON_PATTERNS.some((pattern) => reason.includes(pattern));
};

const finalizeReleasedChannel = (channel) => {
  if (channel.listeners.size > 0) return;
  if (channel.releaseTimer || channel.connectInFlight) {
    return;
  }
  if (channel.ws) {
    return;
  }
  if (channels.get(channel.key) === channel) {
    channels.delete(channel.key);
  }
};

const scheduleReleaseChannelClose = (channel, reason = 'no_listeners', context = {}) => {
  if (!channel) return;
  if (channel.listeners.size > 0) {
    debugChannelEvent('releaseChannel:skip-listeners', channel, {
      reason,
      listenersSize: channel.listeners.size,
      source: context.source ?? null,
      subscriberId: context.subscriberId ?? null,
    });
    return;
  }
  if (channel.releaseTimer) {
    debugChannelEvent('releaseChannel:skip-existing-timer', channel, {
      reason,
      releaseTimerId: channel.releaseTimerId,
      source: context.source ?? null,
      subscriberId: context.subscriberId ?? null,
    });
    return;
  }

  clearReconnectTimer(channel, 'releaseChannel:schedule-close');
  channel.pendingConnect = false;
  channel.pendingRelease = true;
  const releaseTimerId = ++channel.releaseSequence;
  channel.releaseTimerId = releaseTimerId;
  channel.releaseTimer = setTimeout(() => {
    if (channel.releaseTimerId !== releaseTimerId) {
      debugChannelEvent('releaseChannel:skip-stale-timer', channel, {
        reason,
        releaseTimerId,
        source: context.source ?? null,
        subscriberId: context.subscriberId ?? null,
      });
      return;
    }

    channel.releaseTimer = null;
    channel.releaseTimerId = null;

    if (channels.get(channel.key) !== channel) {
      debugChannelEvent('releaseChannel:skip-channel-replaced', channel, {
        reason,
        releaseTimerId,
        source: context.source ?? null,
        subscriberId: context.subscriberId ?? null,
      });
      return;
    }

    if (channel.listeners.size > 0) {
      channel.pendingRelease = false;
      debugChannelEvent('releaseChannel:keep-open-listeners-returned', channel, {
        reason,
        releaseTimerId,
        source: context.source ?? null,
        subscriberId: context.subscriberId ?? null,
      });
      return;
    }

    if (channel.connectInFlight) {
      debugChannelEvent('releaseChannel:keep-open-connect-in-flight', channel, {
        reason,
        releaseTimerId,
        connectInFlight: channel.connectInFlight,
        source: context.source ?? null,
        subscriberId: context.subscriberId ?? null,
      });
      scheduleReleaseChannelClose(channel, 'connect_in_flight_during_release', context);
      return;
    }

    if (channel.reconnectTimer || channel.pendingConnect) {
      debugChannelEvent('releaseChannel:keep-open-reconnect-pending', channel, {
        reason,
        releaseTimerId,
        reconnectTimerId: channel.reconnectTimerId,
        pendingConnect: channel.pendingConnect,
        source: context.source ?? null,
        subscriberId: context.subscriberId ?? null,
      });
      scheduleReleaseChannelClose(channel, 'reconnect_pending_during_release', context);
      return;
    }

    channel.pendingRelease = false;
    debugChannelEvent('releaseChannel:close-after-grace', channel, {
      reason,
      releaseTimerId,
      source: context.source ?? null,
      subscriberId: context.subscriberId ?? null,
    });
    closeSocket(channel, 'no_listeners_grace_elapsed', {
      origin: 'releaseChannel:grace-timer',
      source: context.source ?? null,
      subscriberId: context.subscriberId ?? null,
    });
  }, RELEASE_GRACE_PERIOD_MS);

  debugChannelEvent('releaseChannel:schedule-close', channel, {
    reason,
    releaseTimerId,
    delayMs: RELEASE_GRACE_PERIOD_MS,
    source: context.source ?? null,
    subscriberId: context.subscriberId ?? null,
  });
};

const closeSocket = (channel, reason = 'cleanup', context = {}) => {
  debugChannelEvent('closeSocket', channel, {
    reason,
    origin: context.origin ?? 'closeSocket',
    source: context.source ?? null,
    subscriberId: context.subscriberId ?? null,
  });
  clearReleaseTimer(channel, `closeSocket:${reason}`);
  clearHeartbeatTimer(channel);
  const ws = channel.ws;
  if (!ws) {
    finalizeReleasedChannel(channel);
    return;
  }

  setCloseIntent(channel, {
    internalReason: reason,
    closeCode: 1000,
    origin: context.origin ?? 'closeSocket',
    source: context.source ?? null,
    subscriberId: context.subscriberId ?? null,
  });
  channel.pendingRelease = channel.listeners.size === 0;

  try {
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      closeTrackedSocket(channel, ws, {
        internalReason: reason,
        closeCode: 1000,
        origin: context.origin ?? 'closeSocket',
        source: context.source ?? null,
        subscriberId: context.subscriberId ?? null,
      });
      return;
    }
    if (ws.readyState === WebSocket.CLOSING) {
      return;
    }
  } catch {
    // Socket may already be closed.
  }

  if (channel.ws === ws) {
    channel.ws = null;
  }
  clearCloseIntent(channel);
  finalizeReleasedChannel(channel);
};

const scheduleReconnect = (
  channel,
  reason = 'socket_close',
  { minDelayMs = 0, incrementRetryCount = true } = {}
) => {
  if (channel.listeners.size === 0) {
    debugChannelEvent('scheduleReconnect:skip-no-listeners', channel, { reason });
    return { scheduled: false, reason: 'no_listeners', delayMs: null };
  }
  if (channel.reconnectTimer) {
    debugChannelEvent('scheduleReconnect:skip-existing-timer', channel, {
      reason,
      reconnectTimerId: channel.reconnectTimerId,
    });
    return { scheduled: false, reason: 'existing_timer', delayMs: null };
  }
  if (
    isSocketOpenOrConnecting(channel.ws)
  ) {
    debugChannelEvent('scheduleReconnect:skip-socket-active', channel, { reason });
    return { scheduled: false, reason: 'socket_active', delayMs: null };
  }
  const baseDelay = Math.min(
    MAX_RECONNECT_DELAY_MS,
    BASE_RECONNECT_DELAY_MS * 2 ** channel.retryCount
  );
  const delay = Math.max(baseDelay, Number(minDelayMs) || 0);
  const jitteredDelay = applyReconnectJitter(delay);
  const reconnectTimerId = ++channel.reconnectSequence;
  channel.reconnectTimerId = reconnectTimerId;
  channel.reconnectTimer = setTimeout(() => {
    if (channel.reconnectTimerId !== reconnectTimerId) {
      debugChannelEvent('scheduleReconnect:skip-stale-timer', channel, {
        reason,
        reconnectTimerId,
      });
      return;
    }
    channel.reconnectTimer = null;
    channel.reconnectTimerId = null;
    if (channel.listeners.size === 0) {
      debugChannelEvent('scheduleReconnect:skip-fired-no-listeners', channel, {
        reason,
        reconnectTimerId,
      });
      return;
    }
    if (
      isSocketOpenOrConnecting(channel.ws)
    ) {
      debugChannelEvent('scheduleReconnect:skip-fired-socket-active', channel, {
        reason,
        reconnectTimerId,
      });
      return;
    }
    connectChannel(channel, `reconnect_timer:${reason}`);
  }, jitteredDelay);
  if (incrementRetryCount) {
    channel.retryCount += 1;
  }
  debugChannelEvent('scheduleReconnect', channel, {
    reason,
    delayMs: jitteredDelay,
    reconnectTimerId,
    retryCount: channel.retryCount,
  });
  return { scheduled: true, reason, delayMs: jitteredDelay, reconnectTimerId };
};

const scheduleThrottledReconnect = (channel, reason) => {
  const lastAttemptAt = Number(channel.lastConnectAttemptAt) || 0;
  const remainingMs = MIN_CONNECT_ATTEMPT_INTERVAL_MS - (Date.now() - lastAttemptAt);
  if (remainingMs <= 0) {
    return false;
  }
  debugChannelEvent('connect:throttled', channel, {
    reason,
    remainingMs,
  });
  scheduleReconnect(channel, `throttle:${reason}`, {
    minDelayMs: remainingMs,
    incrementRetryCount: false,
  });
  return true;
};

function connectChannel(channel, reason = 'direct') {
  debugChannelEvent('connect:start', channel, { reason });
  if (channel.listeners.size === 0) {
    debugChannelEvent('connectChannel:skip-no-listeners', channel, { reason });
    return;
  }
  if (!channel.companyId) {
    debugChannelEvent('connectChannel:skip-no-company', channel, { reason });
    return;
  }

  if (channel.connectInFlight) {
    debugChannelEvent('connect:skip-connect-in-flight', channel, {
      reason,
      connectInFlight: channel.connectInFlight,
    });
    return;
  }

  clearReleaseTimer(channel, `connectChannel:${reason}`);

  if (channel.ws) {
    if (isSocketOpenOrConnecting(channel.ws)) {
      debugChannelEvent('connect:reuse-existing-socket', channel, {
        reason,
        reuseReason: resolveReadyState(channel.ws),
      });
      return;
    }
    if (channel.ws.readyState === WebSocket.CLOSING) {
      channel.pendingConnect = true;
      debugChannelEvent('connectChannel:pending-closing-socket', channel, { reason });
      return;
    }
  }

  if (scheduleThrottledReconnect(channel, reason)) {
    return;
  }

  if (channel.status !== STATUS.CONNECTING && channel.status !== STATUS.CONNECTED) {
    channel.status = STATUS.CONNECTING;
    notifyStatus(channel);
  }

  clearReconnectTimer(channel, `connectChannel:${reason}`);
  channel.pendingConnect = false;
  channel.pendingRelease = false;
  channel.manualCloseReason = null;
  clearCloseIntent(channel);
  const clientId = getChannelClientId(channel);
  const socketId = ++channel.socketSequence;
  let ws;
  beginConnectAttempt(channel, socketId, reason);
  try {
    ws = new WebSocket(buildWsUrl(channel.companyId, clientId));
  } catch (error) {
    clearConnectInFlight(channel, socketId, 'constructor_error', {
      reason,
      error: error instanceof Error ? error.message : String(error),
    });
    channel.status = STATUS.DISCONNECTED;
    notifyStatus(channel);
    scheduleReconnect(channel, 'constructor_error');
    return;
  }
  channel.ws = ws;
  channel.activeSocketId = socketId;
  debugChannelEvent('connect:created-new-websocket', channel, {
    reason,
    socketId,
  });

  ws.onopen = () => {
    if (channel.ws !== ws) {
      debugChannelEvent('onopen:stale-socket', channel, { socketId });
      try {
        closeTrackedSocket(channel, ws, {
          internalReason: 'stale_socket_replaced',
          closeCode: 1000,
          origin: 'ws.onopen',
        });
      } catch {
        // Ignore.
      }
      return;
    }
    clearConnectInFlight(channel, socketId, 'onopen');
    // Accepting a socket does not mean its handshake is stable. Keep backoff
    // across sockets which are accepted and immediately closed by the server.
    channel.openedAt = Date.now();
    channel.pendingConnect = false;
    channel.manualCloseReason = null;
    clearCloseIntent(channel);
    channel.status = STATUS.CONNECTED;
    debugChannelEvent('onopen', channel, { socketId });
    startHeartbeatTimer(channel, ws);

    if (channel.listeners.size === 0) {
      scheduleReleaseChannelClose(channel, 'onopen_without_listeners');
      return;
    }

    channel.pendingRelease = false;
    notifyStatus(channel);
  };

  ws.onmessage = (event) => {
    if (channel.ws !== ws) return;
    const payload = toPayload(event?.data);
    notifyMessage(channel, payload);
  };

  ws.onerror = () => {
    if (channel.ws !== ws) {
      debugChannelEvent('onerror:stale-socket', channel, { socketId });
      clearConnectInFlight(channel, socketId, 'onerror:stale');
      return;
    }
    debugChannelEvent('onerror', channel, { socketId });
    try {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        closeTrackedSocket(channel, ws, {
          internalReason: 'error_recovery',
          closeCode: null,
          origin: 'ws.onerror',
        });
        return;
      }
    } catch {
      // Ignore and finalize below.
    }
    clearConnectInFlight(channel, socketId, 'onerror:finalized');
    if (channel.ws === ws) {
      channel.ws = null;
    }
    clearCloseIntent(channel);
    channel.status = STATUS.DISCONNECTED;
    notifyStatus(channel);
    scheduleReconnect(channel, 'error_without_close');
  };

  ws.onclose = (event) => {
    const wasCurrentSocket = channel.ws === ws;
    clearConnectInFlight(channel, socketId, 'onclose', {
      code: event?.code ?? null,
      reason: event?.reason ?? '',
    });
    if (!wasCurrentSocket) {
      debugChannelEvent('onclose:stale-socket', channel, {
        socketId,
        code: event?.code ?? null,
        reason: event?.reason ?? '',
      });
      finalizeReleasedChannel(channel);
      return;
    }
    clearHeartbeatTimer(channel);
    if (channel.openedAt && Date.now() - channel.openedAt >= STABLE_CONNECTION_MS) {
      channel.retryCount = 0;
    }
    channel.openedAt = null;
    if (wasCurrentSocket) {
      channel.ws = null;
    }
    channel.status = STATUS.DISCONNECTED;
    notifyStatus(channel);

    const hadPendingConnect = channel.pendingConnect;
    channel.pendingConnect = false;
    const manualCloseReason = channel.manualCloseReason;
    const closeIntent = channel.closeIntent;
    channel.manualCloseReason = null;
    const shouldReconnect = shouldReconnectForCloseEvent(event);
    let reconnectPlan = { scheduled: false, delayMs: null, reason: null };

    if (channel.listeners.size === 0) {
      channel.pendingRelease = false;
      debugChannelEvent('socket:onclose', channel, {
        socketId,
        code: event?.code ?? null,
        reason: event?.reason ?? '',
        wasClean: event?.wasClean ?? null,
        hadPendingConnect,
        manualCloseReason,
        closeIntent,
        shouldReconnect,
        willReconnect: false,
        reconnectDelayMs: null,
        reconnectTrigger: 'none',
        wasCurrentSocket,
      });
      clearCloseIntent(channel);
      finalizeReleasedChannel(channel);
      return;
    }

    if (hadPendingConnect) {
      reconnectPlan = { scheduled: true, delayMs: 0, reason: 'pending_connect_after_close' };
    } else if (shouldReconnect) {
      reconnectPlan =
        scheduleReconnect(channel, `close_code_${event?.code ?? 'unknown'}`) || reconnectPlan;
    }

    debugChannelEvent('socket:onclose', channel, {
      socketId,
      code: event?.code ?? null,
      reason: event?.reason ?? '',
      wasClean: event?.wasClean ?? null,
      hadPendingConnect,
      manualCloseReason,
      closeIntent,
      shouldReconnect,
      willReconnect: Boolean(reconnectPlan.scheduled),
      reconnectDelayMs: reconnectPlan.delayMs,
      reconnectTrigger: hadPendingConnect
        ? 'pending_connect_after_close'
        : reconnectPlan.scheduled
          ? 'onclose_schedule_reconnect'
          : 'none',
      wasCurrentSocket,
    });

    if (hadPendingConnect) {
      connectChannel(channel, 'pending_connect_after_close');
      clearCloseIntent(channel);
      return;
    }

    clearCloseIntent(channel);
  };
}

const ensureChannel = (companyId) => {
  const normalizedCompanyId = normalizeCompanyId(companyId);
  if (normalizedCompanyId === null) return null;
  const key = String(normalizedCompanyId);
  if (channels.has(key)) {
    const existing = channels.get(key);
    debugChannelEvent('ensureChannel:reuse', existing);
    return existing;
  }
  const channel = {
    key,
    companyId: normalizedCompanyId,
    clientId: resolveDashboardClientId(),
    ws: null,
    listeners: new Set(),
    retryCount: 0,
    openedAt: null,
    reconnectTimer: null,
    heartbeatTimer: null,
    status: STATUS.DISCONNECTED,
    pendingConnect: false,
    pendingRelease: false,
    manualCloseReason: null,
    closeIntent: null,
    connectInFlight: null,
    lastConnectAttemptAt: 0,
    socketSequence: 0,
    activeSocketId: null,
    reconnectSequence: 0,
    reconnectTimerId: null,
    releaseTimer: null,
    releaseSequence: 0,
    releaseTimerId: null,
  };
  channels.set(key, channel);
  debugChannelEvent('ensureChannel:create', channel);
  return channel;
};

const releaseChannel = (channel, context = {}) => {
  if (!channel || channel.listeners.size > 0) {
    debugChannelEvent('releaseChannel:skip-listeners', channel, {
      listenersSize: channel?.listeners?.size ?? 0,
      source: context.source ?? null,
      subscriberId: context.subscriberId ?? null,
    });
    return;
  }
  debugChannelEvent('releaseChannel', channel, {
    reason: 'unsubscribe_last_listener',
    source: context.source ?? null,
    debugLabel: context.debugLabel ?? null,
    subscriberId: context.subscriberId ?? null,
  });
  scheduleReleaseChannelClose(channel, 'unsubscribe_last_listener', context);
};

export function subscribeConversationEvents(companyId, listenerConfig = {}, options = {}) {
  const channel = ensureChannel(companyId);
  if (!channel) return () => {};

  const { onMessage, onStatus, source: legacySource, debugLabel: legacyDebugLabel } =
    listenerConfig || {};
  const source = options.source || legacySource || 'anonymous';
  const debugLabel = options.debugLabel || legacyDebugLabel || null;
  const subscriberId = generateSubscriberId();
  const listener = {
    onMessage,
    onStatus,
    source,
    debugLabel,
    subscriberId,
  };
  let unsubscribed = false;
  const listenersBefore = channel.listeners.size;
  channel.listeners.add(listener);
  clearReleaseTimer(channel, `subscribe:${listener.source}#${listener.subscriberId}`);
  debugChannelEvent('subscribe:add', channel, {
    source: listener.source,
    debugLabel: listener.debugLabel,
    subscriberId: listener.subscriberId,
    listenersBefore,
    listenersAfter: channel.listeners.size,
  });
  safeCall(onStatus, channel.status);
  connectChannel(channel, `subscribe:${listener.source}#${listener.subscriberId}`);

  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    const unsubscribeBefore = channel.listeners.size;
    channel.listeners.delete(listener);
    const unsubscribeAfter = channel.listeners.size;
    debugChannelEvent('unsubscribe:called', channel, {
      source: listener.source,
      debugLabel: listener.debugLabel,
      subscriberId: listener.subscriberId,
      listenersBefore: unsubscribeBefore,
      listenersAfter: unsubscribeAfter,
      willScheduleGraceClose: unsubscribeAfter === 0,
    });
    if (channel.listeners.size === 0) {
      releaseChannel(channel, {
        source: listener.source,
        debugLabel: listener.debugLabel,
        subscriberId: listener.subscriberId,
      });
    }
  };
}
